import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, CheckCircle, Clock, Coins, Download, ExternalLink, Film,
  FolderKanban, Loader2, Play, RefreshCw, Search, Users, Video, X,
} from 'lucide-react';
import { AdminShell, type AdminShellRole } from '@rehub/admin/ui';
import {
  downloadRenderFile,
  fetchAdminProjects,
  getFreshSignedUrl,
  type AdminPropertyCutProject,
  type AdminPropertyCutRender,
} from '../services/supabase';

interface AdminPanelProps {
  adminRole: AdminShellRole;
  onClose: () => void;
}

type AdminSection = 'users' | 'videos';
type ViewFilter = 'finished' | 'in_progress' | 'failed' | 'all';

const IN_PROGRESS = new Set(['draft', 'editing', 'rendering']);
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  editing: 'Editing',
  rendering: 'Rendering',
  render_complete: 'Complete',
  render_failed: 'Failed',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Complete',
  failed: 'Failed',
};

const FORMAT_LABEL: Record<string, string> = {
  vertical: '9:16',
  square: '1:1',
  landscape: '16:9',
};

function hasFinishedVideo(project: AdminPropertyCutProject): boolean {
  return project.renders.some((render) => render.status === 'completed' && (render.output_path || render.output_url));
}

function latestRender(project: AdminPropertyCutProject): AdminPropertyCutRender | null {
  const finished = project.renders.find((render) => render.status === 'completed' && (render.output_path || render.output_url));
  return finished ?? project.renders[0] ?? null;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '--:--';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
}

function formatBytes(bytes: number | null | undefined): string | null {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function statusClass(status: string): string {
  if (status === 'render_complete' || status === 'completed' || status === 'ready') return 'text-green-400';
  if (status === 'render_failed' || status === 'failed') return 'text-red-400';
  if (status === 'rendering' || status === 'processing' || status === 'queued') return 'text-amber-400';
  return 'text-slate-400';
}

interface Playable {
  title: string;
  subtitle: string;
  path: string | null;
  url: string | null;
  portrait: boolean;
}

function PlayerModal({ item, onClose }: { item: Playable; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const signed = await getFreshSignedUrl(item.path, item.url);
      if (cancelled) return;
      if (signed) setUrl(signed);
      else setError('Could not load this video. Admin storage access may not be applied yet.');
    })();
    return () => { cancelled = true; };
  }, [item]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl flex flex-col items-center" onClick={(event) => event.stopPropagation()}>
        <div className="w-full flex items-center justify-between mb-3 gap-3">
          <div className="min-w-0">
            <p className="text-white font-medium truncate">{item.title}</p>
            <p className="text-xs text-slate-400 truncate">{item.subtitle}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        {error ? (
          <p className="text-sm text-red-400 py-20 text-center max-w-md">{error}</p>
        ) : url ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className={`rounded-xl bg-black ${item.portrait ? 'max-h-[80vh] w-auto' : 'w-full max-h-[80vh]'}`}
          />
        ) : (
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin my-20" />
        )}
      </div>
    </div>
  );
}

function ProjectDetail({
  project,
  onClose,
  onPlay,
}: {
  project: AdminPropertyCutProject;
  onClose: () => void;
  onPlay: (item: Playable) => void;
}) {
  const videos = project.assets.filter((asset) => asset.kind === 'video');
  const portrait = project.output_format === 'vertical';

  const playRender = (render: AdminPropertyCutRender) => {
    onPlay({
      title: project.title,
      subtitle: `${project.owner_email ?? project.user_id} · ${FORMAT_LABEL[render.format] ?? render.format}`,
      path: render.output_path,
      url: render.output_url,
      portrait: render.format === 'vertical' || portrait,
    });
  };

  const downloadRender = async (render: AdminPropertyCutRender) => {
    const url = await getFreshSignedUrl(render.output_path, render.output_url);
    if (!url) return;
    await downloadRenderFile({
      outputPath: render.output_path,
      outputUrl: url,
      filename: `PropertyCuts - ${project.title} - ${render.format}.mp4`,
    });
  };

  return (
    <div className="fixed inset-0 z-[100000] bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="w-full max-w-3xl my-8 bg-slate-950 border border-slate-800 rounded-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-800">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{project.title}</h3>
            <p className="text-sm text-slate-400 truncate">{project.owner_email ?? project.user_id}</p>
            {project.owner_name && <p className="text-xs text-slate-500">{project.owner_name}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {[
              ['Status', STATUS_LABEL[project.status] ?? project.status],
              ['Format', project.output_format ? (FORMAT_LABEL[project.output_format] ?? project.output_format) : '—'],
              ['Credits', String(project.credits_charged)],
              ['Address', project.listing_address ?? '—'],
              ['Price', project.listing_price ?? '—'],
              ['Agent', project.agent_name ?? '—'],
              ['Agent email', project.agent_email ?? '—'],
              ['Agent phone', project.agent_phone ?? '—'],
              ['Updated', formatWhen(project.updated_at)],
            ].map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wider text-slate-500">{label}</dt>
                <dd className="text-slate-200 truncate" title={value}>{value}</dd>
              </div>
            ))}
          </dl>

          <section>
            <h4 className="text-sm font-medium text-white mb-2">Exports</h4>
            {project.renders.length === 0 ? (
              <p className="text-sm text-slate-500">No exports yet.</p>
            ) : (
              <ul className="space-y-2">
                {project.renders.map((render) => {
                  const playable = render.status === 'completed' && (render.output_path || render.output_url);
                  return (
                    <li key={render.id} className="flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white">{FORMAT_LABEL[render.format] ?? render.format}</p>
                        <p className={`text-xs ${statusClass(render.status)}`}>
                          {STATUS_LABEL[render.status] ?? render.status}
                          {render.output_duration_seconds ? ` · ${formatDuration(render.output_duration_seconds)}` : ''}
                          {render.credits_charged ? ` · ${render.credits_charged} credits` : ''}
                        </p>
                        {render.error_message && <p className="text-[11px] text-red-400 truncate">{render.error_message}</p>}
                      </div>
                      {playable && (
                        <>
                          <button onClick={() => playRender(render)} className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800" title="Play">
                            <Play size={16} />
                          </button>
                          <button onClick={() => downloadRender(render)} className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800" title="Download">
                            <Download size={16} />
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <h4 className="text-sm font-medium text-white mb-2">Source clips</h4>
            {project.assets.length === 0 ? (
              <p className="text-sm text-slate-500">No uploaded media.</p>
            ) : (
              <ul className="space-y-2">
                {project.assets.map((asset) => (
                  <li key={asset.id} className="flex items-center gap-3 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{asset.original_filename || asset.kind}</p>
                      <p className="text-xs text-slate-500">
                        {asset.kind}
                        {asset.duration_seconds ? ` · ${formatDuration(asset.duration_seconds)}` : ''}
                      </p>
                    </div>
                    {asset.kind === 'video' && (
                      <button
                        onClick={() => onPlay({
                          title: asset.original_filename || project.title,
                          subtitle: project.owner_email ?? project.user_id,
                          path: asset.storage_path,
                          url: null,
                          portrait,
                        })}
                        className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800"
                        title="Play source video"
                      >
                        <Play size={16} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {videos.length === 0 && project.assets.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">Photos are listed above. Only video files can be played here.</p>
            )}
          </section>

          <a
            href={`${window.location.origin}/p/${project.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200"
          >
            <ExternalLink size={14} />
            Open project
          </a>
        </div>
      </div>
    </div>
  );
}

function UsersSection({
  projects,
  onOpenUser,
}: {
  projects: AdminPropertyCutProject[];
  onOpenUser: (userId: string) => void;
}) {
  const [search, setSearch] = useState('');

  const users = useMemo(() => {
    const byId = new Map<string, {
      userId: string;
      email: string | null;
      name: string | null;
      projects: number;
      finished: number;
      videos: number;
      credits: number;
      lastActive: string;
    }>();
    for (const project of projects) {
      const row = byId.get(project.user_id) ?? {
        userId: project.user_id,
        email: project.owner_email,
        name: project.owner_name,
        projects: 0,
        finished: 0,
        videos: 0,
        credits: 0,
        lastActive: project.updated_at,
      };
      row.projects += 1;
      row.videos += project.video_count;
      row.credits += project.credits_charged;
      if (hasFinishedVideo(project)) row.finished += 1;
      if (project.updated_at > row.lastActive) row.lastActive = project.updated_at;
      byId.set(project.user_id, row);
    }
    const query = search.trim().toLowerCase();
    return [...byId.values()]
      .filter((user) => {
        if (!query) return true;
        return (
          (user.email ?? '').toLowerCase().includes(query) ||
          (user.name ?? '').toLowerCase().includes(query) ||
          user.userId.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => b.lastActive.localeCompare(a.lastActive));
  }, [projects, search]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search users by email or name..."
          className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
        />
      </div>
      {users.length === 0 ? (
        <p className="text-sm text-slate-500 py-10 text-center">No users found.</p>
      ) : (
        <div className="overflow-x-auto border border-slate-800 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Projects</th>
                <th className="px-4 py-3 font-medium">Finished</th>
                <th className="px-4 py-3 font-medium">Source videos</th>
                <th className="px-4 py-3 font-medium">Credits</th>
                <th className="px-4 py-3 font-medium">Last active</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId} className="border-t border-slate-800">
                  <td className="px-4 py-3 min-w-[200px]">
                    <p className="text-white truncate">{user.email ?? user.userId}</p>
                    {user.name && <p className="text-xs text-slate-500 truncate">{user.name}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{user.projects}</td>
                  <td className="px-4 py-3 text-slate-300">{user.finished}</td>
                  <td className="px-4 py-3 text-slate-300">{user.videos}</td>
                  <td className="px-4 py-3 text-slate-300">{user.credits}</td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{formatWhen(user.lastActive)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onOpenUser(user.userId)}
                      className="text-xs font-medium text-teal-300 hover:text-teal-200"
                    >
                      View videos
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VideosSection({
  projects,
  userId,
  onClearUser,
}: {
  projects: AdminPropertyCutProject[];
  userId: string | null;
  onClearUser: () => void;
}) {
  const [filter, setFilter] = useState<ViewFilter>('finished');
  const [search, setSearch] = useState('');
  const [playing, setPlaying] = useState<Playable | null>(null);
  const [detail, setDetail] = useState<AdminPropertyCutProject | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const scoped = useMemo(
    () => (userId ? projects.filter((project) => project.user_id === userId) : projects),
    [projects, userId],
  );
  const ownerLabel = scoped[0]?.owner_email ?? scoped[0]?.owner_name ?? userId;

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return scoped.filter((project) => {
      if (filter === 'finished' && !hasFinishedVideo(project)) return false;
      if (filter === 'in_progress' && !IN_PROGRESS.has(project.status)) return false;
      if (filter === 'failed' && project.status !== 'render_failed' && !project.renders.some((render) => render.status === 'failed')) return false;
      if (!query) return true;
      return (
        project.title.toLowerCase().includes(query) ||
        (project.owner_email ?? '').toLowerCase().includes(query) ||
        (project.owner_name ?? '').toLowerCase().includes(query) ||
        (project.listing_address ?? '').toLowerCase().includes(query)
      );
    });
  }, [scoped, filter, search]);

  const counts = useMemo(() => ({
    finished: scoped.filter(hasFinishedVideo).length,
    inProgress: scoped.filter((project) => IN_PROGRESS.has(project.status)).length,
    failed: scoped.filter((project) => project.status === 'render_failed' || project.renders.some((render) => render.status === 'failed')).length,
    total: scoped.length,
  }), [scoped]);

  const handleDownload = async (project: AdminPropertyCutProject) => {
    const render = latestRender(project);
    if (!render || render.status !== 'completed') return;
    setDownloadingId(project.id);
    try {
      const url = await getFreshSignedUrl(render.output_path, render.output_url);
      if (!url) return;
      await downloadRenderFile({
        outputPath: render.output_path,
        outputUrl: url,
        filename: `PropertyCuts - ${project.title}.mp4`,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const filters: Array<{ id: ViewFilter; label: string; count: number }> = [
    { id: 'finished', label: 'Finished videos', count: counts.finished },
    { id: 'in_progress', label: 'In progress', count: counts.inProgress },
    { id: 'failed', label: 'Failed', count: counts.failed },
    { id: 'all', label: 'All projects', count: counts.total },
  ];

  return (
    <div className="space-y-4">
      {userId && (
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <span>Showing {ownerLabel}</span>
          <button onClick={onClearUser} className="text-teal-300 hover:text-teal-200">Clear</button>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by email, address or project..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filters.map((item) => (
            <button
              key={item.id}
              onClick={() => setFilter(item.id)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                filter === item.id
                  ? 'bg-teal-600/20 text-teal-300 border-teal-500/40'
                  : 'text-slate-400 border-slate-700 hover:text-white hover:bg-slate-800'
              }`}
            >
              {item.label} <span className="text-slate-500">({item.count})</span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <Film className="w-12 h-12 mx-auto text-slate-700 mb-4" />
          <p className="text-slate-300 font-medium mb-1">No projects found</p>
          <p className="text-sm text-slate-500">Try a different filter or search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {filtered.map((project) => {
            const finished = hasFinishedVideo(project);
            const render = latestRender(project);
            const size = formatBytes(render?.output_file_size_bytes);
            const portrait = (render?.format ?? project.output_format) === 'vertical';
            return (
              <div key={project.id} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden flex flex-col">
                <button
                  type="button"
                  onClick={() => finished && render && setPlaying({
                    title: project.title,
                    subtitle: project.owner_email ?? project.user_id,
                    path: render.output_path,
                    url: render.output_url,
                    portrait,
                  })}
                  disabled={!finished}
                  className={`group relative bg-slate-800 flex items-center justify-center overflow-hidden disabled:cursor-default ${portrait ? 'aspect-[9/16]' : 'aspect-video'}`}
                  title={finished ? 'Play video' : 'No finished video yet'}
                >
                  {project.thumbnail_url ? (
                    project.thumbnail_is_video ? (
                      <video src={project.thumbnail_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={project.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )
                  ) : (
                    <Video className="w-10 h-10 text-slate-700" />
                  )}
                  {finished && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
                      <span className="w-12 h-12 rounded-full bg-teal-500/90 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                        <Play className="w-5 h-5 text-slate-950 ml-0.5" />
                      </span>
                    </span>
                  )}
                  {project.output_format && (
                    <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-slate-300">
                      {FORMAT_LABEL[project.output_format] ?? project.output_format}
                    </span>
                  )}
                </button>
                <div className="p-3 space-y-1.5 flex-1 flex flex-col">
                  <button onClick={() => setDetail(project)} className="text-left text-sm font-medium text-white truncate hover:text-teal-200" title={project.title}>
                    {project.title}
                  </button>
                  {project.listing_address && (
                    <p className="text-[11px] text-slate-400 truncate" title={project.listing_address}>{project.listing_address}</p>
                  )}
                  {project.owner_email ? (
                    <a href={`mailto:${project.owner_email}`} className="block text-xs text-teal-300 hover:text-teal-200 truncate">
                      {project.owner_email}
                    </a>
                  ) : (
                    <p className="text-xs text-slate-600 italic truncate">{project.user_id}</p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-medium ${statusClass(project.status)}`}>
                      {STATUS_LABEL[project.status] ?? project.status}
                    </span>
                    <span className="text-xs text-slate-500">
                      {formatDuration(render?.output_duration_seconds)}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {formatWhen(project.created_at)}
                    {project.video_count ? ` · ${project.video_count} video${project.video_count === 1 ? '' : 's'}` : ''}
                    {project.photo_count ? ` · ${project.photo_count} photo${project.photo_count === 1 ? '' : 's'}` : ''}
                    {size ? ` · ${size}` : ''}
                  </p>
                  <div className="flex items-center gap-1 pt-1 mt-auto">
                    {finished && (
                      <button
                        onClick={() => handleDownload(project)}
                        disabled={downloadingId === project.id}
                        className="p-1.5 rounded-md text-slate-400 hover:text-teal-300 hover:bg-slate-800 disabled:opacity-50"
                        title="Download video"
                      >
                        {downloadingId === project.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      onClick={() => setDetail(project)}
                      className="p-1.5 rounded-md text-slate-400 hover:text-teal-300 hover:bg-slate-800"
                      title="Project data"
                    >
                      <FolderKanban className="w-3.5 h-3.5" />
                    </button>
                    <a
                      href={`${window.location.origin}/p/${project.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-md text-slate-400 hover:text-teal-300 hover:bg-slate-800"
                      title="Open project"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {playing && <PlayerModal item={playing} onClose={() => setPlaying(null)} />}
      {detail && (
        <ProjectDetail
          project={detail}
          onClose={() => setDetail(null)}
          onPlay={(item) => { setDetail(null); setPlaying(item); }}
        />
      )}
    </div>
  );
}

export default function AdminPanel({ adminRole, onClose }: AdminPanelProps) {
  const [section, setSection] = useState<AdminSection>('users');
  const [projects, setProjects] = useState<AdminPropertyCutProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setProjects(await fetchAdminProjects());
    } catch (err) {
      console.error('Failed to load admin projects:', err);
      setLoadError(err instanceof Error ? err.message : 'Could not load projects.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    users: new Set(projects.map((project) => project.user_id)).size,
    finished: projects.filter(hasFinishedVideo).length,
    inProgress: projects.filter((project) => IN_PROGRESS.has(project.status)).length,
    credits: projects.reduce((sum, project) => sum + project.credits_charged, 0),
  }), [projects]);

  return (
    <AdminShell<AdminSection>
      scope="PropertyCuts"
      role={adminRole}
      onBack={onClose}
      variant="overlay"
      tabs={[
        { id: 'users', label: 'Users', icon: Users },
        { id: 'videos', label: 'Videos', icon: FolderKanban },
      ]}
      activeTab={section}
      onTabChange={setSection}
      title={section === 'users' ? 'Users' : 'Videos and projects'}
      description={section === 'users'
        ? 'Everyone who has a PropertyCuts project, with their videos and credits'
        : 'Source clips, listing details and finished exports'}
      actions={(
        <button
          onClick={load}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Users', value: stats.users, icon: Users, color: 'text-teal-300' },
          { label: 'Finished videos', value: stats.finished, icon: CheckCircle, color: 'text-green-400' },
          { label: 'In progress', value: stats.inProgress, icon: Clock, color: 'text-amber-400' },
          { label: 'Credits charged', value: stats.credits, icon: Coins, color: 'text-orange-400' },
        ].map((stat) => (
          <div key={stat.label} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
            <stat.icon className={`w-4 h-4 mx-auto mb-1 ${stat.color}`} />
            <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</p>
          </div>
        ))}
      </div>

      {loading && projects.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : loadError ? (
        <div className="text-center py-16">
          <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
          <p className="text-sm text-red-300">{loadError}</p>
        </div>
      ) : section === 'users' ? (
        <UsersSection
          projects={projects}
          onOpenUser={(id) => { setUserId(id); setSection('videos'); }}
        />
      ) : (
        <VideosSection projects={projects} userId={userId} onClearUser={() => setUserId(null)} />
      )}

      {projects.length >= 500 && (
        <p className="text-xs text-slate-500 text-center">Showing the 500 most recent projects.</p>
      )}
    </AdminShell>
  );
}
